import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import Select, { StylesConfig, OnChangeValue } from 'react-select';
import { toast } from 'sonner';
import { eachDayOfInterval, format, isAfter } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { DateRange, DateRangePicker, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// Import hooks
import useGetAllRoomType from '../../../services/room-type/hooks/useGetAllRoomType';
import useIndexRatePlan from '../../../services/rate-plan/hooks/useIndexRatePlan';
import { useUpdateAvailabilityRestriction } from '../../../services/availability-and-rates/hooks/useUpdateAvailabilityRestriction';
import { useUpdateRestriction } from '../../../services/availability-and-rates/hooks/useUpdateRestriction';
import { SelectedCell } from './MonthlySetupTable';
import CurrencyInputRef from '../../../shared/components/form/CurrencyInputRef';
import clsx from 'clsx';
import { useMediaQuery } from '../../../shared/hooks/useMediaQuery';

// Opsi untuk dropdown restriksi
const restrictionOptions = [
    { value: 'rate', label: 'Rate', type: 'number' },
    { value: 'closed_to_arrival', label: 'CTA (Closed to Arrival)', type: 'boolean' },
    { value: 'closed_to_departure', label: 'CTD (Closed to Departure)', type: 'boolean' },
    { value: 'stop_sell', label: 'Stop Sell', type: 'boolean' },
    { value: 'max_stay', label: 'Max Stay', type: 'number' },
    { value: 'min_stay_arrival', label: 'Min Stay Arrival', type: 'number' },
    { value: 'min_stay_through', label: 'Min Stay Through', type: 'number' },
];

// Definisi tipe
type SelectOption = { value: string; label: string; };
type RestrictionOption = SelectOption & { type: string; };
type RatePlanSelectOption = SelectOption & {
    parentId: string | null;
    ratePlanType: 'link' | 'rate';
    inheritRestriction: number;
};

const customStyles: StylesConfig<SelectOption, false> = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

interface BulkUpdateByDateRangeDialogProps {
    show: boolean;
    onClose: () => void;
    // PERBAIKAN: onSuccess sekarang menerima array dari sel yang terpengaruh
    onSuccess: (affectedCells: SelectedCell[]) => void;
}

const BulkUpdateByDateRangeDialog: React.FC<BulkUpdateByDateRangeDialogProps> = ({ show, onClose, onSuccess }) => {
    // State untuk form
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [restrictionType, setRestrictionType] = useState<RestrictionOption | null>(null);
    const [restrictionValue, setRestrictionValue] = useState<string | number | boolean>('');
    const [selectedRoomType, setSelectedRoomType] = useState<SelectOption | null>(null);
    const [selectedRatePlan, setSelectedRatePlan] = useState<RatePlanSelectOption | null>(null);

    // BARU: State dan Ref untuk Date Range Picker
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection'
        }
    ]);

    // Inisialisasi hooks
    const queryClient = useQueryClient();
    const { data: roomData, isLoading: isLoadingRoomTypes } = useGetAllRoomType({ all: true });
    const { data: ratePlanData, isLoading: isLoadingRatePlans } = useIndexRatePlan({
        roomTypeId: selectedRoomType?.value,
    });
    const { mutate: updateAvailability, isPending: isUpdatingAvailability } = useUpdateAvailabilityRestriction();
    const { mutate: updateRestriction, isPending: isUpdatingRestriction } = useUpdateRestriction();
    const isPending = isUpdatingAvailability || isUpdatingRestriction;

    // BARU: useEffect untuk menutup kalender saat klik di luar
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [calendarRef]);

    // Opsi untuk dropdown
    const roomTypeOptions: SelectOption[] = useMemo(() => {
        if (!roomData?.data) return [];
        return roomData.data.map(rt => ({ value: rt.id, label: rt.name }));
    }, [roomData]);

    const availableRatePlanOptions = useMemo<RatePlanSelectOption[]>(() => {
        if (!ratePlanData?.data) return [];
        return ratePlanData.data
            .map(rp => ({
                value: rp.id,
                label: rp.name,
                parentId: rp.parent_id,
                ratePlanType: rp.type,
                inheritRestriction: rp.inherit_restriction,
            }));
    }, [ratePlanData]);

    // Filter opsi restriksi berdasarkan rate plan yang dipilih
    const filteredRestrictionOptions = useMemo(() => {
        if (!selectedRatePlan) return restrictionOptions;

        let options = [...restrictionOptions];

        // Jika linked rate (parent_id tidak null), hapus opsi "rate"
        if (selectedRatePlan.parentId !== null) {
            options = options.filter(o => o.value !== 'rate');
        }

        return options;
    }, [selectedRatePlan]);

    // Jika inherit_restriction = 1, maka setup type tidak boleh diubah
    const isSetupTypeDisabled = selectedRatePlan?.inheritRestriction === 1;

    // BARU: Handler untuk perubahan Date Range Picker
    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (selection.startDate && selection.endDate) {
            setDateRange([{
                startDate: selection.startDate,
                endDate: selection.endDate,
                key: selection.key || 'selection'
            }]);
            setStartDate(format(selection.startDate, 'yyyy-MM-dd'));
            setEndDate(format(selection.endDate, 'yyyy-MM-dd'));
        }
    };

    const handleUpdate = async () => {
        if (!startDate || !endDate || !restrictionType || !selectedRoomType || !selectedRatePlan) {
            toast.error("Please fill all required fields, including Room Type and Rate Plan.");
            return;
        }
        if (isAfter(new Date(startDate), new Date(endDate))) {
            toast.error("Start date cannot be after end date.");
            return;
        }
        if (restrictionValue === '') {
            toast.error("Please provide a value for the restriction.");
            return;
        }

        toast.loading('Applying bulk update...', { id: 'bulk-update' });

        const handleSuccess = async () => {
            // 'price-adjustment' WAJIB ikut supaya baris DPM ikut ter-update —
            // queryKey-nya tidak mengandung 'monthly'/'occupancy'.
            await queryClient.refetchQueries({
                predicate: (query) => query.queryKey.some((key) => ['monthly', 'occupancy', 'price-adjustment'].includes(key as string))
            });
            toast.success('Bulk update applied successfully!', { id: 'bulk-update' });
            const affectedDates = eachDayOfInterval({
                start: new Date(startDate),
                end: new Date(endDate)
            });

            const affectedCells: SelectedCell[] = affectedDates.map(date => ({
                rateId: selectedRatePlan!.value, // `!` karena sudah divalidasi
                date: format(date, 'yyyy-MM-dd'),
                rateType: 'rate',
                restrictionType: restrictionType!.value,
            }));

            onSuccess(affectedCells);
        };

        const handleError = (error: any) => {
            const message = error?.response?.data?.message || error.message || "An unknown error occurred.";
            toast.error(message, { id: 'bulk-update' });
        };

        if (restrictionType.value === 'rate') {
            updateAvailability({
                values: [{
                    date_from: startDate,
                    date_to: endDate,
                    rate_plan_id: selectedRatePlan.value,
                    type: restrictionType.value,
                    rate: Number(restrictionValue),
                }]
            }, {
                onSuccess: handleSuccess,
                onError: handleError,
            });
        } else {
            let valueToSend: string;
            if (typeof restrictionValue === 'boolean') {
                valueToSend = restrictionValue ? "1" : "0";
            } else {
                valueToSend = String(restrictionValue);
            }

            updateRestriction({
                values: {
                    rate_plan_id: selectedRatePlan.value,
                    type: restrictionType.value as any,
                    value: valueToSend,
                    date_ranges: [{
                        date_from: startDate,
                        date_to: endDate
                    }],
                }
            }, {
                onSuccess: handleSuccess,
                onError: handleError,
            });
        }
    };

    const renderValueInput = () => {
        if (!restrictionType) return null;

        if (restrictionType.value === 'rate') {
            return (
                <CurrencyInputRef
                    name='newValue'
                    placeholder='Rp 0'
                    className={clsx("form-control")}
                    onChange={(_value, rawValue) => setRestrictionValue(rawValue)}
                    value={restrictionValue as number ?? 0}
                />
            );
        }

        if (restrictionType.type === 'number') {
            return (
                <Form.Control
                    type="number"
                    placeholder={`Enter value for ${restrictionType.label}`}
                    value={restrictionValue as number}
                    onChange={(e) => setRestrictionValue(Number(e.target.value))}
                />
            );
        }

        if (restrictionType.type === 'boolean') {
            return (
                <Form.Check
                    type="switch"
                    id={`switch-${restrictionType.value}`}
                    label={`Set ${restrictionType.label} to "Active"`}
                    checked={!!restrictionValue}
                    onChange={(e) => setRestrictionValue(e.target.checked)}
                />
            );
        }

        return null;
    };

    const handleRoomTypeChange = (option: OnChangeValue<SelectOption, false>) => {
        setSelectedRoomType(option as SelectOption | null);
        setSelectedRatePlan(null);
    };

    const handleRatePlanChange = (option: OnChangeValue<RatePlanSelectOption, false>) => {
        setSelectedRatePlan(option as RatePlanSelectOption | null);
        // Reset restriction type saat rate plan berubah karena opsi yang tersedia bisa berbeda
        setRestrictionType(null);
        setRestrictionValue('');
    };

    const handleRestrictionChange = (option: OnChangeValue<RestrictionOption, false>) => {
        setRestrictionType(option as RestrictionOption | null);
        setRestrictionValue(option?.type === 'boolean' ? false : '');
    };

    // Desktop: floating popover under the input. Mobile: in-flow and full width —
    // an absolutely positioned 2-month picker is wider than the screen and gets
    // cut off on the left.
    const calendarContainerStyle: React.CSSProperties = isMobile
        ? {
            position: 'relative',
            marginTop: '8px',
            width: '100%',
            border: '1px solid #dfe2e5',
            borderRadius: '8px',
            overflow: 'hidden',
        }
        : {
            position: 'absolute',
            zIndex: 1000,
            top: '100%',
            right: 0,
            marginTop: '8px',
            border: '1px solid #dfe2e5',
            borderRadius: '8px',
            boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
            maxWidth: '100%',
            overflow: 'hidden',
        };


    return (
        <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Bulk Update by Date Range</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small">This action will update a specific restriction for one Rate Plan within the selected date range.</p>
                <Form>
                    {/* BARU: Form Group untuk Date Range Picker */}
                    <Form.Group className="mb-3">
                        <Form.Label>Date Range</Form.Label>
                        <div className="position-relative" ref={calendarRef}>
                            <div className="input-group">
                                <input
                                    type='text'
                                    readOnly
                                    className='form-control'
                                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                                    value={startDate && endDate ? `${format(new Date(startDate), 'dd MMM yyyy')} - ${format(new Date(endDate), 'dd MMM yyyy')}` : ''}
                                    placeholder="Select date range"
                                />
                                <span className="input-group-text" onClick={() => setIsCalendarOpen(!isCalendarOpen)} style={{ cursor: 'pointer' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                                        <path d="M16 3l0 4"></path>
                                        <path d="M8 3l0 4"></path>
                                        <path d="M4 11l16 0"></path>
                                        <path d="M8 15h2v2h-2z"></path>
                                    </svg>
                                </span>
                            </div>
                            {isCalendarOpen && (
                                <div style={calendarContainerStyle}>
                                    {isMobile ? (
                                        <DateRange
                                            onChange={handleDateChange}
                                            moveRangeOnFirstSelection={false}
                                            ranges={dateRange}
                                            months={1}
                                            direction="horizontal"
                                        />
                                    ) : (
                                        <DateRangePicker
                                            onChange={handleDateChange}
                                            moveRangeOnFirstSelection={false}
                                            ranges={dateRange}
                                            months={2}
                                            direction="horizontal"
                                            inputRanges={[]}
                                            staticRanges={[]}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3"><Form.Label>Room Type</Form.Label>
                                <Select
                                    options={roomTypeOptions}
                                    value={selectedRoomType}
                                    onChange={handleRoomTypeChange}
                                    styles={customStyles}
                                    menuPortalTarget={document.body}
                                    isLoading={isLoadingRoomTypes}
                                    isDisabled={isLoadingRoomTypes}
                                    placeholder={isLoadingRoomTypes ? "Loading room types..." : "Select a room type..."}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3"><Form.Label>Rate Plan</Form.Label>
                                <Select<RatePlanSelectOption, false>
                                    options={availableRatePlanOptions}
                                    value={selectedRatePlan}
                                    onChange={handleRatePlanChange}
                                    styles={customStyles as unknown as StylesConfig<RatePlanSelectOption, false>}
                                    menuPortalTarget={document.body}
                                    isLoading={isLoadingRatePlans}
                                    isDisabled={!selectedRoomType || isLoadingRatePlans}
                                    placeholder={isLoadingRatePlans ? "Loading rate plans..." : (selectedRoomType ? "Select a rate plan..." : "Select a room type first")}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3"><Form.Label>Setup Type</Form.Label>
                                <Select<RestrictionOption, false>
                                    options={filteredRestrictionOptions}
                                    value={restrictionType}
                                    onChange={handleRestrictionChange}
                                    styles={customStyles as any}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    isDisabled={!selectedRatePlan || isSetupTypeDisabled}
                                    placeholder={isSetupTypeDisabled ? "Inherited from parent" : "Select setup type..."}
                                />
                                {isSetupTypeDisabled && (
                                    <Form.Text className="text-warning">
                                        This rate plan inherits all restrictions from its parent and cannot be modified.
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            {restrictionType && (<Form.Group><Form.Label>New Value</Form.Label>{renderValueInput()}</Form.Group>)}
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isPending}>Close</Button>
                <Button variant="primary" onClick={handleUpdate} disabled={isPending}>{isPending ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Apply Update'}</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BulkUpdateByDateRangeDialog;